import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface CreditPackage {
  credits: number;
  price: number;
  label: string;
  priceLabel: string;
}

interface CreditBalance {
  balance: number;
  total_purchased: number;
  total_used: number;
  active_credits: number;
}

interface CreditsPurchaseProps {
  onClose: () => void;
}

const CreditsPurchase: React.FC<CreditsPurchaseProps> = ({ onClose }) => {
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [packagesRes, balanceRes] = await Promise.all([
        fetch('/api/credits/packages', { headers: getAuthHeaders() }),
        fetch('/api/credits/balance', { headers: getAuthHeaders() })
      ]);

      if (packagesRes.ok && balanceRes.ok) {
        const packagesData = await packagesRes.json();
        const balanceData = await balanceRes.json();
        setPackages(packagesData);
        setBalance(balanceData);
        setSelectedPackage(packagesData[0]?.credits);
      }
    } catch (error) {
      console.error('Error fetching credit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!selectedPackage) return;

    setPurchasing(true);
    try {
      const response = await fetch('/api/credits/purchase', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ credits: selectedPackage, tolt_referral: (window as any).tolt_referral || undefined })
      });

      if (response.ok) {
        const { url } = await response.json();
        // Redirect to Stripe checkout
        window.location.href = url;
      } else {
        alert('Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert('Failed to start purchase. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const calculatePricePerCredit = (pkg: CreditPackage) => {
    return (pkg.price / 100 / pkg.credits).toFixed(4);
  };

  if (loading) {
    return createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="text-center">Loading...</div>
        </div>
      </div>,
      document.body
    );
  }

  const selectedPkg = packages.find(p => p.credits === selectedPackage);

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <span className="text-2xl">💳</span>
            <h2 className="text-2xl font-bold">Select Credits</h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-gray-600">Current Balance</p>
              <p className="text-2xl font-bold text-blue-600">{balance?.balance.toLocaleString()} credits</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {/* Credit Selector */}
        <div className="p-6">
          {/* What Can You Do With Credits */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">What Can You Do With Credits?</h3>

            {/* Email Enrichment */}
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-start gap-3">
                <span className="text-2xl">📧</span>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2">Email Enrichment (FullEnrich)</h4>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p>• Work Email: <span className="font-semibold">1 credit</span></p>
                    <p>• Personal Email: <span className="font-semibold">3 credits</span></p>
                    <p>• Mobile Number: <span className="font-semibold">12 credits</span></p>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">✓ Only charged on successful finds</p>
                </div>
              </div>
            </div>

            {/* Data Enrichment */}
            <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-100">
              <div className="flex items-start gap-3">
                <span className="text-2xl">👤</span>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2">Data Enrichment (Bring Your Own Data)</h4>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p>• <span className="font-semibold">1 credit</span> per profile enriched</p>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">✓ Upload CSV and enrich candidate data</p>
                </div>
              </div>
            </div>

            {/* Company Enrichment */}
            <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-100">
              <div className="flex items-start gap-3">
                <span className="text-2xl">🏢</span>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2">Company Enrichment</h4>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p>• <span className="font-semibold">1 credit</span> per unique company</p>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">✓ Enrich company data for your candidates</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6 pt-6 border-t">
            <h3 className="text-xl font-semibold mb-2">
              Select Your Package
            </h3>
            <p className="text-sm text-gray-600 flex items-center gap-1">
              <span>✓</span> Credits valid 6 months
            </p>
          </div>

          {/* Slider */}
          <div className="mb-8">
            <div className="flex justify-between text-xs text-gray-600 mb-2">
              <span>5k</span>
              <span>10k</span>
              <span>15k</span>
              <span>25k</span>
              <span>50k</span>
              <span>100k</span>
              <span>&gt;100k</span>
            </div>
            <input
              type="range"
              min="0"
              max={packages.length - 1}
              value={packages.findIndex(p => p.credits === selectedPackage)}
              onChange={(e) => setSelectedPackage(packages[parseInt(e.target.value)].credits)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(packages.findIndex(p => p.credits === selectedPackage) / (packages.length - 1)) * 100}%, #e5e7eb ${(packages.findIndex(p => p.credits === selectedPackage) / (packages.length - 1)) * 100}%, #e5e7eb 100%)`
              }}
            />
          </div>


          {/* Package Options */}
          <div className="space-y-3 mb-6">
            {packages.map((pkg) => (
              <div
                key={pkg.credits}
                onClick={() => setSelectedPackage(pkg.credits)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedPackage === pkg.credits
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      checked={selectedPackage === pkg.credits}
                      onChange={() => setSelectedPackage(pkg.credits)}
                      className="w-5 h-5"
                    />
                    <div>
                      <p className="font-semibold">{pkg.label}</p>
                      <p className="text-sm text-gray-600">
                        Valid for 6 months
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">{pkg.priceLabel}</p>
                    <p className="text-xs text-gray-500">No Subscription</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Purchase Button */}
          <button
            onClick={handlePurchase}
            disabled={purchasing || !selectedPackage}
            className="w-full bg-blue-600 text-white py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {purchasing ? 'Processing...' : `Purchase ${selectedPkg?.label} →`}
          </button>

          {/* Features */}
          <div className="mt-8 pt-8 border-t">
            <h4 className="font-semibold mb-4">Waterfall enrichment with 20+ data sources, including:</h4>
            <div className="flex flex-wrap gap-3 mb-6 text-sm">
              <span className="px-3 py-1 bg-gray-100 rounded-full">🔍 Hunter</span>
              <span className="px-3 py-1 bg-gray-100 rounded-full">✨ Wiza</span>
              <span className="px-3 py-1 bg-gray-100 rounded-full">📊 Datagma</span>
              <span className="px-3 py-1 bg-gray-100 rounded-full">💼 ContactOut</span>
              <span className="px-3 py-1 bg-gray-100 rounded-full">👥 PeopleDataLabs</span>
              <span className="px-3 py-1 bg-gray-100 rounded-full text-blue-600">+20 other data sources</span>
              <span className="px-3 py-1 bg-green-100 rounded-full text-green-700 font-semibold">✓ &gt;80% average find rate</span>
            </div>

            <h4 className="font-semibold mb-4">Included in all plans</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-green-600">✓</span>
                <span>Triple Email Verification</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-600">✓</span>
                <span>Credit only used on success</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-600">✓</span>
                <span>Unlimited users</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-600">✓</span>
                <span>Phone Verification</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreditsPurchase;
